import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableDropdownBox } from '@/components/SearchableDropdownBox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Plus, Trash2, ChevronDown, ChevronUp, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { computeToolStats, computePhaseLife, computeDeviation } from '@/utils/toolLifeCalculations';

const statusColors = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-300',
  Submitted: 'bg-blue-100 text-blue-700 border-blue-300',
  Approved: 'bg-green-100 text-green-700 border-green-300',
  Rejected: 'bg-red-100 text-red-700 border-red-300',
};

/** Phase life for row ci = pieces from this row to the next (life achieved in this row's phase). */
function checkpointLife(checkpoints, ci) {
  if (ci < 0 || !checkpoints.length || ci >= checkpoints.length - 1) return null;
  return Math.abs((checkpoints[ci + 1].qty ?? 0) - (checkpoints[ci].qty ?? 0));
}

function formatPhase(phase, totalPhases = 6) {
  if (phase == null || phase === '') return '—';
  const total = totalPhases ?? 6;
  return phase === 1 ? 'N' : `${phase}/${total}`;
}

const PLACEHOLDER_TOOL = '_placeholder_tool_';
const PLACEHOLDER_INSERT = '_placeholder_insert_';
const PLACEHOLDER_PHASE = '_select_phase_';

function DeviationBadge({ deviation }) {
  if (!deviation) return <span className="text-slate-300">-</span>;
  if (deviation.status === 'ok') return <span className="text-green-600 font-medium">OK</span>;
  if (deviation.status === 'below') return <span className="text-red-600 font-medium">{deviation.value}</span>;
  return <span className="text-amber-600 font-medium">+{deviation.value}</span>;
}

function CheckpointTable({ checkpoints, locked, machineId, toolIndex, totalPhases = 6, standardRange, onUpdate, onRemove }) {
  const total = totalPhases ?? 6;
  const phaseOptions = Array.from({ length: total - 1 }, (_, i) => i + 2);
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-100/50">
            <TableHead className="w-12 text-xs">#</TableHead>
            <TableHead className="text-xs">QTY (cumulative)</TableHead>
            <TableHead className="text-xs">Phase</TableHead>
            <TableHead className="text-xs">Phase Life</TableHead>
            <TableHead className="text-xs">Std Range</TableHead>
            <TableHead className="text-xs">Deviation</TableHead>
            {!locked && <TableHead className="w-10"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {checkpoints.map((cp, ci) => {
            const life = checkpointLife(checkpoints, ci);
            const deviation = computeDeviation(life, standardRange);
            const phase = cp.insert_phase;
            const phaseValue = (phase >= 1 && phase <= total) ? String(phase) : PLACEHOLDER_PHASE;
            const phaseError = phase == null || phase === '' || phase < 1 || phase > total;
            const prevQty = ci > 0 ? (checkpoints[ci - 1].qty ?? 0) : 0;
            const currentQty = cp.qty ?? 0;
            const isNegative = currentQty < 0;
            const notGreaterThanPrev = ci > 0 && currentQty <= prevQty;
            const qtyError = isNegative || notGreaterThanPrev;
            return (
              <TableRow key={ci}>
                <TableCell className="text-xs text-slate-400 py-1">{ci + 1}</TableCell>
                <TableCell className="py-1">
                  <div className="space-y-0.5">
                    <Input
                      type="number"
                      min={ci > 0 ? prevQty + 1 : 0}
                      value={cp.qty !== undefined && cp.qty !== null && cp.qty !== 0 ? cp.qty : ''}
                      onChange={e => {
                        const raw = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        const v = raw === null || Number.isNaN(raw) ? null : Math.max(0, raw);
                        onUpdate(ci, 'qty', v === null ? 0 : v);
                      }}
                      disabled={locked}
                      className={`h-8 w-20 text-sm ${qtyError ? 'border-red-400 bg-red-50 focus-visible:ring-red-400' : ''}`}
                      data-testid={`qty-input-${machineId}-${toolIndex}-${ci}`}
                      aria-invalid={qtyError}
                    />
                    {qtyError && (
                      <p className="text-xs text-red-600" role="alert">
                        {isNegative ? 'Cannot be negative' : `Must be > previous (${prevQty})`}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-1">
                  <div className="space-y-0.5">
                    <Select
                      value={phaseValue}
                      onValueChange={v => onUpdate(ci, 'insert_phase', v === PLACEHOLDER_PHASE ? undefined : parseInt(v))}
                      disabled={locked}
                      data-testid={`phase-select-${machineId}-${toolIndex}-${ci}`}
                    >
                      <SelectTrigger
                        className={`h-8 w-24 text-sm ${phaseError ? 'border-red-400 bg-red-50 focus-visible:ring-red-400' : ''}`}
                        aria-invalid={phaseError}
                      >
                        <SelectValue placeholder="Select phase" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={PLACEHOLDER_PHASE}>Select phase</SelectItem>
                        <SelectItem value="1">N</SelectItem>
                        {phaseOptions.map(p => (
                          <SelectItem key={p} value={String(p)}>{p}/{total}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {phaseError && (
                      <p className="text-xs text-red-600" role="alert">
                        Phase is required
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-1">
                  <span className="text-sm font-mono font-medium text-slate-700">
                    {life !== null ? life : <span className="text-slate-300">-</span>}
                  </span>
                </TableCell>
                <TableCell className="py-1">
                  <span className="text-xs font-mono text-slate-500">
                    {standardRange ? `${standardRange.min}\u2013${standardRange.max}` : <span className="text-slate-300">N/A</span>}
                  </span>
                </TableCell>
                <TableCell className="py-1 text-xs font-mono">
                  <DeviationBadge deviation={deviation} />
                </TableCell>
                {!locked && (
                  <TableCell className="py-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => onRemove(ci)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function ToolSummaryPanel({ tool, totalPhases = 6, standardRange }) {
  const stats = computeToolStats(tool.checkpoints);
  const total = totalPhases ?? 6;
  if (tool.checkpoints.length < 2) return null;
  const avgDeviation = computeDeviation(stats.avgLife, standardRange);
  return (
    <div className="bg-white border border-slate-200 rounded p-2 text-xs space-y-0.5">
      <div className="font-medium text-slate-700 mb-1">
        Tool {tool.tool_code} {tool.insert_type && `\u2014 ${tool.insert_type}`}
      </div>
      {tool.checkpoints.map((cp, ci) => {
        if (ci === 0) return null;
        const life = computePhaseLife(tool.checkpoints, ci);
        const deviation = computeDeviation(life, standardRange);
        return (
          <div key={ci} className="flex items-center gap-2 text-slate-600">
            <span className="flex-1">Phase {formatPhase(tool.checkpoints[ci - 1].insert_phase, total)} {'\u2192'} {formatPhase(cp.insert_phase, total)}</span>
            <span className="font-mono font-medium w-16 text-right">{life} pcs</span>
            <span className="w-12 text-right"><DeviationBadge deviation={deviation} /></span>
          </div>
        );
      })}
      {standardRange && (
        <div className="border-t border-slate-100 pt-1 mt-1 text-slate-500">
          Standard: {standardRange.min}{'\u2013'}{standardRange.max} pcs
        </div>
      )}
      <div className={`${standardRange ? 'pt-0.5' : 'border-t border-slate-100 pt-1 mt-1'} flex justify-between font-medium`}>
        <span>Inserts Used: {stats.totalInserts}</span>
        <span className="flex items-center gap-1.5">
          Avg Life: {stats.avgLife !== null ? stats.avgLife.toFixed(1) : '-'} pcs
          {avgDeviation && <DeviationBadge deviation={avgDeviation} />}
        </span>
      </div>
    </div>
  );
}

function ToolEntryRow({ tool, toolIndex, machineCode, toolMasters, insertTypes, locked, standardRange, onUpdateTool, onRemoveTool, onAddCheckpoint, onUpdateCheckpoint, onRemoveCheckpoint }) {
  const stats = computeToolStats(tool.checkpoints);
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50/50">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={tool.tool_code ? String(tool.tool_code) : PLACEHOLDER_TOOL} onValueChange={v => onUpdateTool('tool_code', v === PLACEHOLDER_TOOL ? '' : v)} disabled={locked}>
          <SelectTrigger className="h-9 w-32" data-testid={`tool-code-select-${machineCode}-${toolIndex}`}>
            <SelectValue placeholder="Select tool" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={PLACEHOLDER_TOOL}>Select tool</SelectItem>
            {toolMasters.filter(tm => tm.tool_code).map(tm => (
              <SelectItem key={tm.tool_code} value={tm.tool_code}>{tm.tool_code} - {tm.tool_description}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tool.insert_type ? String(tool.insert_type) : PLACEHOLDER_INSERT} onValueChange={v => onUpdateTool('insert_type', v === PLACEHOLDER_INSERT ? '' : v)} disabled={locked}>
          <SelectTrigger className="h-9 w-36" data-testid={`insert-type-select-${machineCode}-${toolIndex}`}>
            <SelectValue placeholder="Select insert type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={PLACEHOLDER_INSERT}>Select insert type</SelectItem>
            {insertTypes.filter(it => it.insert_type_code).map(it => (
              <SelectItem key={it.insert_type_code} value={it.insert_type_code}>{it.insert_type_code}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!locked && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={onRemoveTool}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
        <div className="ml-auto flex items-center gap-3 text-xs">
          <span className="text-slate-500">Inserts: <strong className="text-slate-800">{stats.totalInserts}</strong></span>
          <span className="text-slate-500">Avg Life: <strong className="text-slate-800">{stats.avgLife !== null ? stats.avgLife.toFixed(1) : '-'}</strong></span>
        </div>
      </div>

      {tool.checkpoints.length > 0 && (
        <CheckpointTable
          checkpoints={tool.checkpoints}
          locked={locked}
          machineId={machineCode}
          toolIndex={toolIndex}
          totalPhases={insertTypes.find(it => it.insert_type_code === tool.insert_type)?.total_phases ?? 6}
          standardRange={standardRange}
          onUpdate={onUpdateCheckpoint}
          onRemove={onRemoveCheckpoint}
        />
      )}

      {!locked && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={onAddCheckpoint}
          disabled={!tool.tool_code || !tool.insert_type}
          title={(!tool.tool_code || !tool.insert_type) ? 'Select Tool and Insert type first' : undefined}
          data-testid={`add-checkpoint-${machineCode}-${toolIndex}`}
        >
          <Plus className="w-3 h-3 mr-1" /> Add Checkpoint
        </Button>
      )}
      {!locked && (!tool.tool_code || !tool.insert_type) && (
        <p className="text-xs text-amber-600">Select Tool and Insert type to add checkpoints</p>
      )}

      <ToolSummaryPanel tool={tool} totalPhases={insertTypes.find(it => it.insert_type_code === tool.insert_type)?.total_phases ?? 6} standardRange={standardRange} />
    </div>
  );
}

function getStandardRange(toolLifeStandards, partId, toolCode, insertType, setupStage) {
  if (!toolLifeStandards?.length || !partId || !toolCode || !insertType) return null;
  const std = toolLifeStandards.find(s =>
    s.part_id === partId &&
    s.tool_code === toolCode.toUpperCase() &&
    s.insert_type_code === insertType.toUpperCase()
  );
  if (!std) return null;
  if (setupStage === 'II') {
    return { min: std.frequency_cnc_ind_setup_min, max: std.frequency_cnc_ind_setup_max };
  }
  return { min: std.frequency_1st_setup_min, max: std.frequency_1st_setup_max };
}

export function MachineToolCard({
  machine, operation, status, locked, expanded, data, parts, toolMasters, insertTypes,
  toolLifeStandards,
  onToggle, onUpdateField, onAddTool, onRemoveTool, onUpdateTool,
  onAddCheckpoint, onUpdateCheckpoint, onRemoveCheckpoint
}) {
  const active = machine.active !== false;
  const canExpand = active;
  const handleHeaderClick = () => {
    if (canExpand) onToggle();
    else toast.info('This machine is inactive. You can view its past logs in Inspection History.');
  };
  return (
    <Card className={`border-slate-200 ${locked ? 'opacity-75' : ''} ${!active ? 'opacity-60' : ''}`} data-testid={`machine-card-${machine.code}`}>
      <CardHeader
        className={`pb-3 ${canExpand ? 'cursor-pointer' : 'cursor-pointer'}`}
        onClick={handleHeaderClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">{machine.code}</CardTitle>
            <Badge variant="outline" className={operation === 'OBR' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
              {operation}
            </Badge>
            {status && (
              <Badge variant="outline" className={statusColors[status] || ''} data-testid={`status-badge-${machine.code}`}>
                {status}
              </Badge>
            )}
            {!active && (
              <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-300">
                Inactive
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">{data.tools.length} tool(s)</span>
            {canExpand && (expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />)}
          </div>
        </div>
      </CardHeader>

      {canExpand && expanded && (
        <CardContent className="pt-0 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Setup Stage</Label>
              <Select
                value={data.setupStage ?? 'I'}
                onValueChange={v => onUpdateField('setupStage', v)}
                disabled={locked}
                data-testid={`setup-select-${machine.code}`}
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="Setup" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="I">Setup I</SelectItem>
                  <SelectItem value="II">Setup II</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Operation</Label>
              <Select
                value={data.operation ?? 'OBR'}
                onValueChange={v => onUpdateField('operation', v)}
                disabled={locked}
                data-testid={`operation-select-${machine.code}`}
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="Operation" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OBR">OBR</SelectItem>
                  <SelectItem value="OTR">OTR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Part No</Label>
              <SearchableDropdownBox
                options={parts.map(p => ({ value: p.id, label: p.part_no }))}
                value={data.partNoId || ''}
                onChange={id => onUpdateField('partNoId', id)}
                placeholder="Select Part No"
                disabled={locked}
                searchPlaceholder="Type to search part no..."
                emptyMessage="No part found."
                triggerTestId={`part-select-${machine.code}`}
              />
            </div>
          </div>

          {data.tools.map((tool, ti) => (
            <ToolEntryRow
              key={ti}
              tool={tool}
              toolIndex={ti}
              machineCode={machine.code}
              toolMasters={toolMasters}
              insertTypes={insertTypes}
              locked={locked}
              standardRange={getStandardRange(toolLifeStandards, data.partNoId, tool.tool_code, tool.insert_type, data.setupStage)}
              onUpdateTool={(field, value) => onUpdateTool(ti, field, value)}
              onRemoveTool={() => onRemoveTool(ti)}
              onAddCheckpoint={() => onAddCheckpoint(ti)}
              onUpdateCheckpoint={(ci, field, value) => onUpdateCheckpoint(ti, ci, field, value)}
              onRemoveCheckpoint={(ci) => onRemoveCheckpoint(ti, ci)}
            />
          ))}

          {!locked && (
            <Button
              variant="outline"
              size="sm"
              onClick={onAddTool}
              disabled={!data.partNoId}
              title={!data.partNoId ? 'Select Part No first' : undefined}
              data-testid={`add-tool-${machine.code}`}
            >
              <Wrench className="w-3.5 h-3.5 mr-1.5" /> Add Tool
            </Button>
          )}
          {!locked && !data.partNoId && (
            <p className="text-xs text-amber-600">Select Part No to add a tool</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
